import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, CheckCircle, Building2, Users, FlaskConical, BookOpen, Mic2, Library, Clock } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import Modal from '../components/layout/Modal';

// ── SHARED STYLES ─────────────────────────────────────────────────────────────
const inputCls =
  'w-full px-3 py-2.5 bg-white border border-[#E2E0D8] rounded-xl text-[13px] ' +
  'text-[#1a1a18] placeholder-[#B4B2A9] focus:outline-none focus:ring-2 ' +
  'focus:ring-[#1a1a18]/10 focus:border-[#888780] transition-colors';
const labelCls = 'block text-[11px] font-medium text-[#888780] uppercase tracking-wider mb-1.5';

// ── ROOMS PAGE ────────────────────────────────────────────────────────────────
const EMPTY_ROOM = { name: '', building: '', capacity: '', roomType: 'classroom' };
const ROOM_TYPES = ['classroom', 'lab', 'hall', 'other_off_campus', 'library'];

const ROOM_TYPE_STYLE = {
  classroom:  { bg: '#E6F1FB', text: '#185FA5', icon: BookOpen },
  lab:        { bg: '#EAF3DE', text: '#3B6D11', icon: FlaskConical },
  hall: { bg: '#EEEDFE', text: '#534AB7', icon: Building2 },
  other_off_campus:    { bg: '#FAEEDA', text: '#854F0B', icon: Users },
  library:    { bg: '#F1EFE8', text: '#5F5E5A', icon: Library },
};

function RoomTypePill({ type }) {
  const s = ROOM_TYPE_STYLE[type] || { bg: '#F1EFE8', text: '#5F5E5A' };
  return (
    <span
      className="inline-block px-2.5 py-1 rounded-lg text-[11px] font-medium capitalize"
      style={{ background: s.bg, color: s.text }}
    >
      {type}
    </span>
  );
}

export function RoomsPage() {
  const qc = useQueryClient();
  const [modal, setModal] = useState({ open: false, mode: 'create', data: null });

  const { data: rooms = [], isLoading } = useQuery(
    ['rooms'],
    () => api.get('/rooms').then(r => r.data)
  );

  const saveMutation = useMutation(
    (form) =>
      modal.mode === 'create'
        ? api.post('/rooms', form)
        : api.put(`/rooms/${modal.data.id}`, form),
    {
      onSuccess: () => {
        qc.invalidateQueries(['rooms']);
        toast.success('Saved!');
        setModal({ open: false });
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Failed to save'),
    }
  );

  const deleteMutation = useMutation(
    (id) => api.delete(`/rooms/${id}`),
    {
      onSuccess: () => { qc.invalidateQueries(['rooms']); toast.success('Deleted'); },
      onError: () => toast.error('Cannot delete — may be in use'),
    }
  );

  return (
    <div className="min-h-screen bg-[#F7F6F2]">
      {/* Header */}
      <header className="bg-[#F7F6F2] border-b border-[#E2E0D8] px-8 py-4
                         flex items-center justify-between sticky top-0 z-10">
        <div>
          <p className="text-[10px] text-[#B4B2A9] uppercase tracking-[0.12em] mb-0.5">
            Timetabling System
          </p>
          <h1 className="text-[18px] font-medium text-[#1a1a18] leading-none">Rooms &amp; Venues</h1>
        </div>
        <button
          onClick={() => setModal({ open: true, mode: 'create', data: EMPTY_ROOM })}
          className="flex items-center gap-2 px-4 py-2 bg-[#1a1a18] hover:bg-[#2C2C2A]
                     text-[#F7F6F2] text-[13px] font-medium rounded-xl transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> Add Room
        </button>
      </header>

      <main className="px-8 py-7 max-w-7xl mx-auto">
        {/* Stats strip */}
        <div className="flex items-center gap-3 mb-6">
          <span className="text-[12px] text-[#888780]">
            {rooms.length} {rooms.length === 1 ? 'room' : 'rooms'} registered
          </span>
          {ROOM_TYPES.map(t => {
            const count = rooms.filter(r => r.room_type === t).length;
            if (!count) return null;
            const s = ROOM_TYPE_STYLE[t] || { bg: '#F1EFE8', text: '#5F5E5A' };
            return (
              <span
                key={t}
                className="px-2 py-0.5 rounded-lg text-[11px] font-medium capitalize"
                style={{ background: s.bg, color: s.text }}
              >
                {count} {t}
              </span>
            );
          })}
        </div>

        {/* Grid */}
        {isLoading ? (
          <p className="text-[13px] text-[#B4B2A9]">Loading…</p>
        ) : rooms.length === 0 ? (
          <div className="bg-white border border-[#E2E0D8] rounded-2xl flex flex-col items-center gap-3 py-20">
            <div className="w-10 h-10 rounded-xl bg-[#F1EFE8] flex items-center justify-center">
              <Building2 className="w-5 h-5 text-[#B4B2A9]" />
            </div>
            <p className="text-[13px] text-[#B4B2A9]">No rooms added yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {rooms.map(r => {
              const s = ROOM_TYPE_STYLE[r.room_type] || { bg: '#F1EFE8', text: '#5F5E5A', icon: Building2 };
              const Icon = s.icon;
              return (
                <div
                  key={r.id}
                  className="group bg-white border border-[#E2E0D8] rounded-2xl p-5
                             hover:border-[#C8C6BE] hover:shadow-sm transition-all"
                >
                  {/* Top row */}
                  <div className="flex items-start justify-between mb-4">
                    <div
                      className="w-8 h-8 rounded-xl flex items-center justify-center"
                      style={{ background: s.bg }}
                    >
                      <Icon className="w-4 h-4" style={{ color: s.text }} />
                    </div>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => setModal({ open: true, mode: 'edit', data: { ...r, roomType: r.room_type } })}
                        className="p-1.5 text-[#B4B2A9] hover:text-[#1a1a18] hover:bg-[#F1EFE8] rounded-lg transition-colors"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => { if (window.confirm('Delete this room?')) deleteMutation.mutate(r.id); }}
                        className="p-1.5 text-[#B4B2A9] hover:text-[#A32D2D] hover:bg-[#FCEBEB] rounded-lg transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  {/* Info */}
                  <p className="text-[14px] font-semibold text-[#1a1a18] leading-tight">{r.name}</p>
                  {r.building && (
                    <p className="text-[12px] text-[#888780] mt-1">{r.building}</p>
                  )}

                  {/* Footer */}
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#F1EFE8]">
                    <RoomTypePill type={r.room_type} />
                    {r.capacity && (
                      <span className="flex items-center gap-1 text-[11px] text-[#B4B2A9]">
                        <Users className="w-3 h-3" /> {r.capacity}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      <Modal
        isOpen={modal.open}
        onClose={() => setModal({ open: false })}
        title={modal.mode === 'create' ? 'Add Room' : 'Edit Room'}
        size="sm"
      >
        <RoomForm
          initial={modal.data}
          loading={saveMutation.isLoading}
          onSubmit={saveMutation.mutate}
          onCancel={() => setModal({ open: false })}
        />
      </Modal>
    </div>
  );
}

function RoomForm({ initial, loading, onSubmit, onCancel }) {
  const [form, setForm] = useState(initial || EMPTY_ROOM);
  const set = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  return (
    <form onSubmit={e => { e.preventDefault(); onSubmit(form); }} className="space-y-4 pt-1">
      <div>
        <label className={labelCls}>Room Name *</label>
        <input required value={form.name} onChange={set('name')} className={inputCls} placeholder="Hall A" />
      </div>
      <div>
        <label className={labelCls}>Building</label>
        <input value={form.building} onChange={set('building')} className={inputCls} placeholder="Main Block" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Capacity</label>
          <input type="number" value={form.capacity} onChange={set('capacity')} className={inputCls} placeholder="40" />
        </div>
        <div>
          <label className={labelCls}>Type</label>
          <select value={form.roomType} onChange={set('roomType')} className={inputCls}>
            {ROOM_TYPES.map(t => (
              <option key={t} value={t} className="capitalize">{t}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="flex gap-3 pt-2">
        <button
          type="button" onClick={onCancel}
          className="flex-1 py-2.5 bg-white border border-[#E2E0D8] hover:border-[#888780]
                     text-[#5F5E5A] hover:text-[#1a1a18] rounded-xl transition-colors text-[13px] font-medium"
        >
          Cancel
        </button>
        <button
          type="submit" disabled={loading}
          className="flex-1 py-2.5 bg-[#1a1a18] hover:bg-[#2C2C2A] disabled:opacity-50
                     text-[#F7F6F2] rounded-xl transition-colors text-[13px] font-medium"
        >
          {loading ? 'Saving…' : 'Save room'}
        </button>
      </div>
    </form>
  );
}

// ── ACADEMIC YEARS PAGE ───────────────────────────────────────────────────────
const EMPTY_YEAR = { name: '', startDate: '', endDate: '', isActive: false };

export function AcademicYearsPage() {
  const qc = useQueryClient();
  const [modal, setModal] = useState({ open: false, data: null });

  const { data: years = [], isLoading } = useQuery(
    ['years'],
    () => api.get('/academic-years').then(r => r.data)
  );

  const saveMutation = useMutation(
    (form) => api.post('/academic-years', form),
    {
      onSuccess: () => {
        qc.invalidateQueries(['years']);
        toast.success('Year created!');
        setModal({ open: false });
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Failed'),
    }
  );

  const activateMutation = useMutation(
    (id) => api.put(`/academic-years/${id}/activate`),
    {
      onSuccess: () => { qc.invalidateQueries(['years']); toast.success('Active year updated!'); },
      onError: () => toast.error('Failed to activate'),
    }
  );

  const deleteMutation = useMutation(
    (id) => api.delete(`/academic-years/${id}`),
    {
      onSuccess: () => { qc.invalidateQueries(['years']); toast.success('Deleted'); },
      onError: () => toast.error('Cannot delete — has associated timetables'),
    }
  );

  return (
    <div className="min-h-screen bg-[#F7F6F2]">
      {/* Header */}
      <header className="bg-[#F7F6F2] border-b border-[#E2E0D8] px-8 py-4
                         flex items-center justify-between sticky top-0 z-10">
        <div>
          <p className="text-[10px] text-[#B4B2A9] uppercase tracking-[0.12em] mb-0.5">
            Timetabling System
          </p>
          <h1 className="text-[18px] font-medium text-[#1a1a18] leading-none">Academic Years</h1>
        </div>
        <button
          onClick={() => setModal({ open: true })}
          className="flex items-center gap-2 px-4 py-2 bg-[#1a1a18] hover:bg-[#2C2C2A]
                     text-[#F7F6F2] text-[13px] font-medium rounded-xl transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> Add Year
        </button>
      </header>

      <main className="px-8 py-7 max-w-3xl mx-auto">
        <p className="text-[12px] text-[#888780] mb-6">
          {years.length} {years.length === 1 ? 'year' : 'years'} configured
        </p>

        {isLoading ? (
          <p className="text-[13px] text-[#B4B2A9]">Loading…</p>
        ) : (
          <div className="space-y-2.5">
            {years.map(y => (
              <div
                key={y.id}
                className={`group flex items-center justify-between px-6 py-5 rounded-2xl border transition-all
                  ${y.is_active
                    ? 'bg-white border-[#1a1a18]/20 shadow-sm'
                    : 'bg-white border-[#E2E0D8] hover:border-[#C8C6BE]'
                  }`}
              >
                <div className="flex items-center gap-4">
                  {/* Year indicator dot */}
                  <div
                    className={`w-2 h-2 rounded-full flex-shrink-0
                      ${y.is_active ? 'bg-[#1a1a18]' : 'bg-[#E2E0D8]'}`}
                  />
                  <div>
                    <div className="flex items-center gap-2.5">
                      <p className="text-[15px] font-semibold text-[#1a1a18]">{y.name}</p>
                      {y.is_active && (
                        <span
                          className="px-2 py-0.5 rounded-lg text-[10px] font-medium uppercase tracking-wide"
                          style={{ background: '#EAF3DE', color: '#3B6D11' }}
                        >
                          Active
                        </span>
                      )}
                    </div>
                    <p className="text-[12px] text-[#888780] mt-0.5">
                      {new Date(y.start_date).toLocaleDateString('en-GB', { dateStyle: 'long' })}
                      {' '}—{' '}
                      {new Date(y.end_date).toLocaleDateString('en-GB', { dateStyle: 'long' })}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {!y.is_active && (
                    <button
                      onClick={() => activateMutation.mutate(y.id)}
                      className="px-3 py-1.5 bg-[#F7F6F2] hover:bg-[#1a1a18] border border-[#E2E0D8]
                                 hover:border-[#1a1a18] text-[#5F5E5A] hover:text-[#F7F6F2]
                                 text-[12px] font-medium rounded-xl transition-all"
                    >
                      Set active
                    </button>
                  )}
                  <button
                    onClick={() => {
                      if (window.confirm('Delete this year and all its timetables?')) deleteMutation.mutate(y.id);
                    }}
                    className="p-2 text-[#B4B2A9] hover:text-[#A32D2D] hover:bg-[#FCEBEB]
                               rounded-xl transition-colors opacity-0 group-hover:opacity-100"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}

            {years.length === 0 && (
              <div className="bg-white border border-[#E2E0D8] rounded-2xl flex flex-col items-center gap-3 py-20">
                <div className="w-10 h-10 rounded-xl bg-[#F1EFE8] flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-[#B4B2A9]" />
                </div>
                <p className="text-[13px] text-[#B4B2A9]">No academic years created yet</p>
              </div>
            )}
          </div>
        )}
      </main>

      <Modal
        isOpen={modal.open}
        onClose={() => setModal({ open: false })}
        title="Create Academic Year"
        size="sm"
      >
        <YearForm
          loading={saveMutation.isLoading}
          onSubmit={saveMutation.mutate}
          onCancel={() => setModal({ open: false })}
        />
      </Modal>
    </div>
  );
}

function YearForm({ loading, onSubmit, onCancel }) {
  const [form, setForm] = useState(EMPTY_YEAR);
  const set = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  return (
    <form onSubmit={e => { e.preventDefault(); onSubmit(form); }} className="space-y-4 pt-1">
      <div>
        <label className={labelCls}>Year Name *</label>
        <input
          required value={form.name} onChange={set('name')}
          className={inputCls} placeholder="2025/2026"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Start Date *</label>
          <input required type="date" value={form.startDate} onChange={set('startDate')} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>End Date *</label>
          <input required type="date" value={form.endDate} onChange={set('endDate')} className={inputCls} />
        </div>
      </div>
      <label className="flex items-center gap-3 cursor-pointer group">
        <div
          className={`w-9 h-5 rounded-full relative transition-colors
            ${form.isActive ? 'bg-[#1a1a18]' : 'bg-[#E2E0D8]'}`}
          onClick={() => setForm(p => ({ ...p, isActive: !p.isActive }))}
        >
          <div
            className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow-sm
                        transition-transform ${form.isActive ? 'translate-x-4' : 'translate-x-0.5'}`}
          />
        </div>
        <span className="text-[13px] text-[#5F5E5A]">Set as active year</span>
      </label>
      <div className="flex gap-3 pt-2">
        <button
          type="button" onClick={onCancel}
          className="flex-1 py-2.5 bg-white border border-[#E2E0D8] hover:border-[#888780]
                     text-[#5F5E5A] hover:text-[#1a1a18] rounded-xl transition-colors text-[13px] font-medium"
        >
          Cancel
        </button>
        <button
          type="submit" disabled={loading}
          className="flex-1 py-2.5 bg-[#1a1a18] hover:bg-[#2C2C2A] disabled:opacity-50
                     text-[#F7F6F2] rounded-xl transition-colors text-[13px] font-medium"
        >
          {loading ? 'Creating…' : 'Create year'}
        </button>
      </div>
    </form>
  );
}

// ── TIME SLOTS PAGE ───────────────────────────────────────────────────────────
export function TimeSlotsPage() {
  const { data: slots = [], isLoading } = useQuery(
    ['slots'],
    () => api.get('/time-slots').then(r => r.data)
  );

  // Group slots loosely by time of day for visual grouping
  const morning   = slots.filter(s => s.label?.startsWith('0') || s.label?.startsWith('1'));
  const afternoon = slots.filter(s => s.label?.startsWith('1') && parseInt(s.label) >= 12);

  return (
    <div className="min-h-screen bg-[#F7F6F2]">
      {/* Header */}
      <header className="bg-[#F7F6F2] border-b border-[#E2E0D8] px-8 py-4 sticky top-0 z-10">
        <div>
          <p className="text-[10px] text-[#B4B2A9] uppercase tracking-[0.12em] mb-0.5">
            Timetabling System
          </p>
          <h1 className="text-[18px] font-medium text-[#1a1a18] leading-none">Time Slots</h1>
        </div>
      </header>

      <main className="px-8 py-7 max-w-4xl mx-auto">
        <p className="text-[12px] text-[#888780] mb-6">
          Pre-configured scheduling slots · {slots.length} available
        </p>

        {isLoading ? (
          <p className="text-[13px] text-[#B4B2A9]">Loading…</p>
        ) : (
          <div className="bg-white border border-[#E2E0D8] rounded-2xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#F1EFE8]">
                  {['#', 'Label', 'Period'].map(h => (
                    <th
                      key={h}
                      className="text-left text-[10px] font-medium text-[#888780]
                                 uppercase tracking-widest px-6 py-3.5"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {slots.length === 0 ? (
                  <tr>
                    <td colSpan={3}>
                      <div className="flex flex-col items-center gap-3 py-16">
                        <div className="w-10 h-10 rounded-xl bg-[#F1EFE8] flex items-center justify-center">
                          <Clock className="w-5 h-5 text-[#B4B2A9]" />
                        </div>
                        <p className="text-[13px] text-[#B4B2A9]">No time slots configured</p>
                      </div>
                    </td>
                  </tr>
                ) : slots.map((s, i) => {
                  // Naive period label
                  const hour = parseInt(s.label);
                  const period = isNaN(hour) ? '' : hour < 12 ? 'Morning' : hour < 17 ? 'Afternoon' : 'Evening';
                  return (
                    <tr
                      key={s.id}
                      className={`hover:bg-[#FAFAF8] transition-colors
                        ${i < slots.length - 1 ? 'border-b border-[#F1EFE8]' : ''}`}
                    >
                      <td className="px-6 py-3.5">
                        <span className="text-[12px] text-[#B4B2A9] font-mono">
                          {String(s.id).padStart(2, '0')}
                        </span>
                      </td>
                      <td className="px-6 py-3.5">
                        <span className="font-mono text-[13px] font-semibold text-[#1a1a18]">
                          {s.label}
                        </span>
                      </td>
                      <td className="px-6 py-3.5">
                        {period && (
                          <span
                            className="px-2 py-0.5 rounded-lg text-[11px] font-medium"
                            style={
                              period === 'Morning'
                                ? { background: '#FAEEDA', color: '#854F0B' }
                                : period === 'Afternoon'
                                ? { background: '#E6F1FB', color: '#185FA5' }
                                : { background: '#EEEDFE', color: '#534AB7' }
                            }
                          >
                            {period}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}